import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	FiAlertCircle,
	FiDownload,
	FiEdit2,
	FiEye,
	FiEyeOff,
	FiFile,
	FiLoader,
	FiTrash2,
} from "react-icons/fi";

import { cn, shortenDigits } from "utils/helpers";
import { showToast } from "utils/helpers";
import { updateDocumentDisplay } from "newui/features/Company/company-api";
import { companyDocumentKeys } from "../companyDocumentKeys";
import { EditDocumentModal } from "./EditDocumentModal";
import { DeleteDocumentModal } from "./DeleteDocumentModal";
import { PendingVerificationBox } from "newui/features/Insurance/components/PendingVerificationBox";
import { ReplaceFileButton } from "newui/features/Insurance/components/ReplaceFileButton";
import { ValidateHistoricalDocButton } from "newui/features/Insurance/components/ValidateHistoricalDocButton";
import { ReviewDraftModal } from "./ReviewDraftModal";
import type { CorporateDocument } from "newui/features/Company/types";
import type { InsuranceOcrJobState } from "newui/features/Insurance/types/InsuranceOcrJob.type";
import { isDateExpired, isDateWithin30Days } from "utils/helpers";
import dayjs from "newui/utils/dayjs";

const OCR_COMPLETED_STATES: InsuranceOcrJobState[] = ["succeeded", "soft_failed", "failed"];
const OCR_ACTIVE_STATES: InsuranceOcrJobState[] = ["queued", "running"];

const INSURANCE_CERTIFICATES_CATEGORY_ID = 4;

interface EditableDocumentCategoryGroupProps {
	categoryId: number;
	companyId: number;
	documents: CorporateDocument[];
	hasInsuranceVerification: boolean;
	title: string;
}

interface VisibilityToggleProps {
	companyId: number;
	display: "public" | "private";
	documentId: number;
}

const VisibilityToggle: React.FC<VisibilityToggleProps> = ({
	companyId,
	display,
	documentId,
}) => {
	const queryClient = useQueryClient();
	const [optimisticDisplay, setOptimisticDisplay] = useState(display);
	const isPublic = optimisticDisplay === "public";

	// Sync with server state when prop changes
	useEffect(() => {
		setOptimisticDisplay(display);
	}, [display]);

	const mutation = useMutation({
		mutationFn: (newDisplay: "public" | "private") =>
			updateDocumentDisplay(companyId, documentId, newDisplay),
		onMutate: (newDisplay) => {
			setOptimisticDisplay(newDisplay);
		},
		onSuccess: (_data, newDisplay) => {
			queryClient.invalidateQueries({
				queryKey: companyDocumentKeys.documents(companyId),
			});
			showToast(
				`Document set to ${newDisplay}.`,
				"Success",
				true
			);
		},
		onError: () => {
			setOptimisticDisplay(display);
			showToast("Failed to update visibility.", "Error");
		},
	});

	return (
		<button
			type="button"
			onClick={(e) => {
				e.stopPropagation();
				mutation.mutate(isPublic ? "private" : "public");
			}}
			disabled={mutation.isPending}
			className="relative inline-flex h-7 w-[104px] shrink-0 items-center rounded-full border border-gray-200 bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
			title={isPublic ? "Click to make private" : "Click to make public"}
		>
			{/* Track labels */}
			<span
				className={`absolute left-2.5 text-[10px] font-semibold uppercase tracking-wide transition-opacity ${isPublic ? "opacity-0" : "text-gray-400 opacity-100"
					}`}
			>
				Private
			</span>
			<span
				className={`absolute right-2.5 text-[10px] font-semibold uppercase tracking-wide transition-opacity ${isPublic ? "text-gray-400 opacity-100" : "opacity-0"
					}`}
			>
				Public
			</span>
			{/* Thumb */}
			<span
				className={`inline-flex h-6 w-[52px] items-center justify-center gap-1 rounded-full shadow-sm transition-all ${isPublic
					? "translate-x-[50px] bg-blue-500 text-white"
					: "translate-x-0.5 bg-white text-gray-500"
					}`}
			>
				{mutation.isPending ? (
					<FiLoader size={12} className="animate-spin" />
				) : isPublic ? (
					<>
						<FiEye size={12} />
						<span className="text-[10px] font-semibold">Public</span>
					</>
				) : (
					<>
						<FiEyeOff size={12} />
						<span className="text-[10px] font-semibold">Private</span>
					</>
				)}
			</span>
		</button>
	);
};

const ExpiryBadge = ({ expiryDate, hasExpired, isExpiringSoon, }: { expiryDate: string, hasExpired: boolean, isExpiringSoon: boolean }) => {

	if (!hasExpired && !isExpiringSoon) {
		return (
			<span className="text-xs text-gray-400">
				Exp: {expiryDate}
			</span>
		);
	}

	return (
		<span
			className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${hasExpired
				? "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200"
				: "bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-200"
				}`}
		>
			{hasExpired ? "Expired" : "Expires soon"}: {expiryDate}
		</span>
	);
};

export const EditableDocumentCategoryGroup: React.FC<
	EditableDocumentCategoryGroupProps
> = ({ categoryId, companyId, documents, hasInsuranceVerification, title }) => {
	const queryClient = useQueryClient();
	const [editingDoc, setEditingDoc] = useState<CorporateDocument | null>(
		null
	);
	const [deletingDoc, setDeletingDoc] = useState<CorporateDocument | null>(
		null
	);
	const [reviewingDoc, setReviewingDoc] = useState<CorporateDocument | null>(
		null
	);

	const isInsurance =
		categoryId === INSURANCE_CERTIFICATES_CATEGORY_ID ||
		documents.some(
			(d) => d.category?.parent_id === INSURANCE_CERTIFICATES_CATEGORY_ID
		);

	const publishedDocs = documents.filter((d) => !d.is_draft);
	const draftDocs = documents.filter((d) => d.is_draft === true);

	const renderRow = (doc: CorporateDocument) => {
		const isDraft = doc.is_draft === true;
		const hasExpired = isDateExpired(doc?.extra_info?.expiry_date);
		const formattedDate = dayjs(doc?.extra_info?.expiry_date).format("DD-MM-YYYY");
		const isExpiringSoon = !hasExpired && isDateWithin30Days(doc?.extra_info?.expiry_date);
		const insuranceValue =
			isInsurance && doc.extra_info?.value && doc.display !== "private"
				? `$${shortenDigits(doc.extra_info.value)}`
				: null;

		const ocrState = (doc.latest_ocr_job?.state ?? null) as InsuranceOcrJobState | null;
		const isOcrComplete = ocrState !== null && OCR_COMPLETED_STATES.includes(ocrState);
		const isOcrActive = ocrState !== null && OCR_ACTIVE_STATES.includes(ocrState);
		const isDraftClickable = isDraft && ocrState !== null;

		const handleRowClick = () => {
			if (!isDraft) {
				setEditingDoc(doc);
			} else if (isDraftClickable) {
				setReviewingDoc(doc);
			}
		};

		return (
			<div
				key={doc.id}
				role="button"
				tabIndex={0}
				onClick={handleRowClick}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						handleRowClick();
					}
				}}
				className={cn("group flex items-center gap-3 border-b border-gray-100 px-4 py-3 transition-colors last:border-b-0 rounded"
					, (!isDraft || isDraftClickable) && "cursor-pointer"
					, (!isDraft || isDraftClickable) && (hasExpired ? "hover:bg-red-200" : "hover:bg-blue-50")
					, hasExpired && "bg-red-50"
					, isExpiringSoon && "bg-orange-50")
				}
			>
				{
					hasExpired ? <FiAlertCircle className="text-red-600" size={24} />
						: <FiFile className="shrink-0 text-gray-400" size={18} />
				}

				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<p className={cn("truncate text-sm font-medium text-gray-900"
							, hasExpired && "text-red-700"
							, isExpiringSoon && "text-orange-600")}>
							{doc.name}
						</p>
						{isInsurance && insuranceValue && (
							<span className={cn("shrink-0 text-sm font-semibold tabular-nums"
								, hasExpired ? "text-red-300" : isExpiringSoon ? "text-orange-500" : "text-gray-500")}>
								{insuranceValue}
							</span>
						)}
						{isInsurance && doc.extra_info?.expiry_date && !isDraft && (
							<ExpiryBadge expiryDate={formattedDate} hasExpired={hasExpired} isExpiringSoon={isExpiringSoon} />
						)}
						{hasExpired && <div className="hidden tracking-tight group-hover:block text-lg text-red-800 font-extrabold">
							Attention needed
						</div>}
						{isDraft && isOcrActive && (
							<span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
								<FiLoader size={12} className="animate-spin" />
								Processing
							</span>
						)}
						{isDraft && isOcrComplete && (
							<span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
								Ready to Review
							</span>
						)}
						{isDraft && !ocrState && (
							<span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
								Draft
							</span>
						)}
					</div>
					{doc.description && (
						<p className="truncate text-xs text-gray-500">
							{doc.description}
						</p>
					)}

					{isDraft && doc.latest_ocr_job && isOcrActive && (
						<div className="mt-2">
							<PendingVerificationBox
								documentId={doc.id}
								initialState={doc.latest_ocr_job.state as InsuranceOcrJobState}
								onComplete={() => {
									queryClient.invalidateQueries({
										queryKey: companyDocumentKeys.documents(companyId),
									});
								}}
							/>
						</div>
					)}
					{isDraft && isOcrComplete && (
						<p className="mt-1 text-xs text-blue-600">
							Click to review extracted fields
						</p>
					)}
					{isDraft && ocrState === "failed" && (
						<div className="mt-2" onClick={(e) => e.stopPropagation()}>
							<ReplaceFileButton
								documentId={doc.id}
								onStarted={() => {
									queryClient.invalidateQueries({
										queryKey: companyDocumentKeys.documents(companyId),
									});
								}}
							/>
						</div>
					)}
				</div>

				{isDraft && (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							setDeletingDoc(doc);
						}}
						className="shrink-0 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
						title="Delete draft"
					>
						<FiTrash2 size={16} />
					</button>
				)}

				{!isDraft && (
					<>
						<VisibilityToggle
							companyId={companyId}
							display={doc.display}
							documentId={doc.id}
						/>

						{doc.storage_path && (
							<a
								href={doc.storage_path}
								target="_blank"
								rel="noopener noreferrer"
								onClick={(e) => e.stopPropagation()}
								className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1.5 text-xs font-medium text-blue-600 ring-1 ring-inset ring-blue-200 transition-colors hover:bg-blue-50"
							>
								<FiDownload size={14} />
								View
							</a>
						)}

						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								setEditingDoc(doc);
							}}
							className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
							title="Edit document"
						>
							<FiEdit2 size={16} />
						</button>

						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								setDeletingDoc(doc);
							}}
							className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
							title="Delete document"
						>
							<FiTrash2 size={16} />
						</button>

						{isInsurance && hasInsuranceVerification && !doc.latest_ocr_job && !(doc.validated) && (
							<div onClick={(e) => e.stopPropagation()}>
								<ValidateHistoricalDocButton
									documentId={doc.id}
									latestOcrJob={doc.latest_ocr_job ?? null}
									validated={doc.validated ?? false}
									onViewResult={() => setEditingDoc(doc)}
								/>
							</div>
						)}
					</>
				)}
			</div>
		);
	};

	if (documents.length === 0) {
		return (
			<div>
				<h3 className="mb-3 text-sm font-semibold text-gray-900">
					{title}
				</h3>
				<div className="rounded-lg border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-400">
					No documents uploaded to this category.
				</div>
			</div>
		);
	}

	return (
		<div>
			<h3 className="mb-3 text-sm font-semibold text-gray-900">{title}</h3>
			<div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
				{publishedDocs.map(renderRow)}
				{draftDocs.length > 0 && (
					<>
						{publishedDocs.length > 0 && (
							<div className="bg-gray-50 px-4 py-2">
								<p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
									Pending Verification
								</p>
							</div>
						)}
						{draftDocs.map(renderRow)}
					</>
				)}
			</div>

			{editingDoc && (
				<EditDocumentModal
					companyId={companyId}
					document={editingDoc}
					isOpen
					onClose={() => setEditingDoc(null)}
				/>
			)}

			{deletingDoc && (
				<DeleteDocumentModal
					companyId={companyId}
					documentId={deletingDoc.id}
					documentName={deletingDoc.name}
					isOpen
					onClose={() => setDeletingDoc(null)}
				/>
			)}

			{reviewingDoc && (
				<ReviewDraftModal
					companyId={companyId}
					document={reviewingDoc}
					isOpen
					onClose={() => {
						setReviewingDoc(null);
						queryClient.invalidateQueries({
							queryKey: companyDocumentKeys.documents(companyId),
						});
					}}
				/>
			)}
		</div>
	);
};
